import { api } from '@/store/helpers'
import { version } from '../../../../package.json'
import Application from 'App/Foundation/Application'
import { saveToStorage } from 'App/Helpers'
import {
	LOGOUT,
	SET_SESSION,
	SET_USER,
	SET_VERSION,
	RESET_ALERT_PREFERENCES,
} from '@/store/mutation-types'
import util from '@/util'
import {
	LOGIN_EXPIRY_DURATION_MS,
	LOGIN_TIMESTAMP_KEY,
} from '@/config/constants'

/**
 * The module's dispatchable actions
 *
 * @author Alejandro Sanchez <asanchez@claruscare.com>
 */
export default {
	/**
	 * Check & get the current user's authentication status and data.
	 *
	 * @param {Function} options.commit
	 * @param {Function} options.dispatch
	 * @param {Object} options.state
	 * @return {Promise|Boolean}
	 */
	async check({ commit, dispatch, state }) {
		if (!state.session.access_token || !state.user.id) {
			return false
		}

		const sessionValid = await dispatch('checkSessionExpiry')
		if (!sessionValid) {
			return false
		}

		const refreshToken = state?.session?.refresh_token

		const status = await dispatch('checkOnce')
		if (status === 200) return true
		if (status !== 401) return false

		const refreshed = await dispatch('refreshAuthenticationInfo', {
			refresh_token: refreshToken,
		})
		if (!refreshed) return false

		const retryStatus = await dispatch('checkOnce')
		if (retryStatus === 200) {
			return true
		} else {
			commit(LOGOUT)
			commit(`settings/${RESET_ALERT_PREFERENCES}`, null, { root: true })
			return false
		}
	},

	/**
	 * Check if the login session has exceeded 15 days; if so, logout and reset alert preferences.
	 *
	 * @param {Function} options.commit
	 * @return {Promise<boolean>} false if session expired and user was logged out, true otherwise
	 */
	async checkSessionExpiry({ commit }) {
		const loginTimestamp = localStorage.getItem(LOGIN_TIMESTAMP_KEY)
		if (loginTimestamp) {
			const parsed = Number(JSON.parse(loginTimestamp))
			if (
				!Number.isNaN(parsed) &&
				Date.now() - parsed > LOGIN_EXPIRY_DURATION_MS
			) {
				commit(LOGOUT)
				commit(`settings/${RESET_ALERT_PREFERENCES}`, null, {
					root: true,
				})
				return false
			}
		}
		return true
	},

	/**
	 * Fetch current user, apply to store. Returns HTTP status (200) or error status.
	 *
	 * @param {Function} options.dispatch
	 * @return {Promise<number>}
	 */
	async checkOnce({ dispatch }) {
		try {
			const response = await api()
				.users()
				.find()
			const user = response.get('data', {})
			await dispatch('getPartnerInfo', user)
			dispatch('setUser', user)
			return response.getStatusCode()
		} catch (err) {
			return err?.status || 500
		}
	},

	/**
	 * Attempt to authenticate a user with the given credentials.
	 *
	 * @param {Function} options.commit
	 * @param {Function} options.dispatch
	 * @param {Object} payload.username
	 * @param {Object} payload.password
	 * @return {Promise}
	 */
	async login({ commit, dispatch }, { username, password }) {
		const response = await api()
			.auth()
			.login(username, password)

		const {
			access_token,
			expires_in,
			refresh_token,
			token_type,
		} = response.get('data', {})

		const session = {
			access_token,
			expires_in,
			refresh_token,
			token_type,
		}

		await commit(SET_SESSION, session)

		saveToStorage(LOGIN_TIMESTAMP_KEY, Date.now())

		const userData = await api()
			.users()
			.find()

		const user = userData.get('data', {})

		await dispatch('getPartnerInfo', user)
		commit(SET_USER, user)
		dispatch('setUser', user)

		const app = Application.getInstance()

		app.emit('auth:success', app)

		return response
	},

	async refreshAuthenticationInfo({ commit }, payload) {
		try {
			const response = await api()
				.auth()
				.refreshAccessToken(payload)

			const {
				access_token,
				expires_in,
				refresh_token,
				token_type,
			} = response.get('data', {})

			const session = {
				access_token,
				expires_in,
				refresh_token,
				token_type,
			}

			await commit(SET_SESSION, session)
			return true
		} catch (error) {
			return false
		}
	},

	/**
	 * Logout the current user.
	 *
	 * @param {Function} options.commit
	 * @param {Object} [payload]
	 * @param {boolean} [payload.signOutCalled=false] - When true, calls auth API to invalidate session server-side.
	 * @return {Promise<void>}
	 */
	async logout({ commit }) {
		commit(LOGOUT)
		commit(`settings/${RESET_ALERT_PREFERENCES}`, null, { root: true })
	},

	async acknowledgeLogout() {
		try {
			await api()
				.auth()
				.logout()
		} catch (error) {
			console.error('User logout status not updated.')
		}
	},

	/**
	 * Set the authenticated user.
	 *
	 * @param {Function} options.commit
	 * @param {Function} options.dispatch
	 * @param {Object} model
	 * @return {void}
	 */
	async setUser({ commit, dispatch, rootState }, model) {
		const { ...user } = model

		const payload = rootState.partners?.pagination?.isSearching || false

		const partnerList = await dispatch('partners/getPartnerList', null, {
			root: true,
		})

		const newPartnerIds = partnerList
			.get('partners', [])
			.map(partner => partner.id)

		if (payload) {
			user.partner_ids = newPartnerIds
		} else {
			user.partner_ids = Array.from(
				new Set([...(user.partner_ids || []), ...newPartnerIds])
			)
		}

		user.messaging_profile_ids = []

		commit(SET_USER, user)
	},

	async getPartnerInfo({ dispatch, rootState }, user) {
		const { partners } = user

		const sortedPartners = util.sortPartners(partners || [])

		const defaultPartnerId = sortedPartners.length
			? sortedPartners[0].id
			: null

		const activePartnerId = rootState.partners?.active

		if (activePartnerId) {
			await dispatch('partners/find', activePartnerId, { root: true })
		}

		if (!activePartnerId && defaultPartnerId) {
			await dispatch('partners/setActive', defaultPartnerId, {
				root: true,
			})
		}

		if (!activePartnerId && !defaultPartnerId) {
			const page = rootState.partners.pagination.current_page || 1
			const keyword = rootState.partners.pagination.searchKeyword || ''

			const response = await api()
				.partners()
				.page(page)
				.search(keyword)
				.get()

			const sortedPartnersList = util.sortPartners(
				response.get('partners', [])
			)
			const firstPartnerId = sortedPartnersList[0]?.id || null
			await dispatch('partners/setActive', firstPartnerId, {
				root: true,
			})
		}
	},
	/**
	 * Set the current code base version.
	 *
	 * @param {Function} options.commit
	 * @param {Function} options.dispatch
	 * @return {void}
	 */
	async setVersion({ commit }) {
		commit(SET_VERSION, version)
	},
}
